<template>
  <div class="quiz-wrapper">
    <div class="container">
      <div class="quiz-slide-title">
        {{ presentation.name }}
      </div>
    </div>
    <div v-if="isEnableAudienceReviewSlides" class="lookback-content">
      <div class="nav-tabs">
        <div
          class="nav-item"
          :class="{
            active: currentLookbackTab === 'MyQuizAnswers',
          }"
          @click="changeLookbackTab('MyQuizAnswers')"
        >
          {{ $t('my_answers') }}
        </div>
        <div
          class="nav-item"
          :class="{
            active: currentLookbackTab === 'LookbackSharingView',
          }"
          @click="changeLookbackTab('LookbackSharingView')"
        >
          {{ $t('all_slides') }}
        </div>
      </div>
      <MyQuizAnswers
        v-if="currentLookbackTab === 'MyQuizAnswers'"
        :slide="slide"
        :slideIndex="slideIndex"
        :slides="slides"
        :presentation="presentation"
        :audienceId="audienceId"
        :activeTabView="activeTabView"
      />
      <LookbackSharingView
        v-if="currentLookbackTab === 'LookbackSharingView'"
        :presentation="presentation"
        :slides="slides"
        :maxLookbackSlideIndex="maxLookbackSlideIndex"
      />
    </div>
    <EmptyQuizAnswer
      v-else
      :msg="$t('ask_presenter_to_view_slides')"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import QuizStatuses from '@/constants/QuizStatuses'
import util from '@/util'
import LookbackSharingView from './LookbackSharingView.vue'
import MyQuizAnswers from './MyQuizAnswers.vue'
import EmptyQuizAnswer from './EmptyQuizAnswer.vue'

export default {
  components: {
    LookbackSharingView,
    MyQuizAnswers,
    EmptyQuizAnswer,
  },
  props: {
    slide: {
      type: Object,
      required: true,
    },
    slideIndex: {
      type: Number,
      required: true,
    },
    slides: {
      type: Array,
      required: true,
    },
    presentation: {
      type: Object,
      required: true,
    },
    audienceId: {
      type: String,
      required: true,
    },
    activeTabView: {
      type: String,
      required: true,
    },
    maxLookbackSlideIndex: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      currentLookbackTab: 'MyQuizAnswers', // ['MyQuizAnswers', 'LookbackSharingView']
    }
  },
  computed: {
    isQuizEnd() {
      if (!this.slide || !this.slides || !this.slides.length) return false

      return this.slide.type === 'pickAnswer' && this.slide.quizStatus === QuizStatuses.RESULT_SCREEN
    },
    isEnableAudienceReviewSlides() {
      return this.presentation && this.presentation.isEnableAudienceReviewSlides
    },
  },
  watch: {
    slideIndex: {
      handler(value) {
        if (value !== undefined && value !== null && value !== -1) {
          this.handleSetLookbackMaxSlideIndex(value)
        }
      },
      immediate: true,
    },
    isQuizEnd: {
      handler(value) {
        if (value) {
          this.handleSetLookbackMaxSlideIndex(this.slideIndex, true)
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.setCurrentLookbackTabByQueryParam()
  },
  methods: {
    ...mapActions([
      'setMaxLookbackSlideIndexAction',
    ]),
    setCurrentLookbackTabByQueryParam() {
      if (this.$route.query.tab && ['MyQuizAnswers', 'LookbackSharingView'].includes(this.$route.query.tab)) {
        this.currentLookbackTab = this.$route.query.tab
      }
    },
    changeLookbackTab(tab) {
      this.currentLookbackTab = tab

      if (this.$route.name === 'Profile') {
        util.ga4Track('Account > Presentation details > Switch tab', {
          eventLabel: tab === 'MyQuizAnswers'
            ? 'My Answers'
            : 'All Slides',
          presentationId: this.presentation && this.presentation.id,
        })
      }
    },
    handleSetLookbackMaxSlideIndex(index, isQuizEnd = false) {
      if (this.slide.type !== 'pickAnswer') {
        this.setMaxLookbackSlideIndexAction(index)
      } else if (this.slide.type === 'pickAnswer' && isQuizEnd) {
        this.setMaxLookbackSlideIndexAction(index)
      } else {
        this.setMaxLookbackSlideIndexAction(index - 1)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.quiz-wrapper {
  margin: 0 auto;
  padding: 24px 0;

  .container {
    margin: 0 auto;
    max-width: 558px;
  }
}

.quiz-slide-title {
  font-size: 20px;
  font-weight: 700;
  color: #313131;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-wrap: break-word;
}

.nav-tabs {
  border-bottom: 1px solid var(--base-50, #D1D1D1);
  font-size: 14px;
  text-align: center;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  justify-content: center;
  margin-top: 16px;
  margin-bottom: 16px;

  .nav-item {
    padding: 0 6px 16px 6px;
    font-weight: 700;
    min-width: 94px;
    cursor: pointer;
    position: relative;
    color: var(--base-90, #313131);
    text-transform: capitalize;

    &.active {
      color: var(--primary-90, #348EF6);
      cursor: default;

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: var(--primary-90, #348EF6);
      }
    }
  }

  @media screen and (min-width: 480px) {
    grid-template-columns: auto auto;
  }
}
</style>
